import React, { useEffect, useState } from "react";
import { withRouter, Link } from "react-router-dom";
import { Query } from "react-apollo";
import { gql } from "apollo-boost";
import { connect } from "react-redux";
import { useTranslation, Trans } from "react-i18next";
import orderBy from "lodash/orderBy";

import { changeCount, removeOrderPosition } from "../store/actions/order";

import { makeStyles } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Container from "@material-ui/core/Container";
import PostAddIcon from "@material-ui/icons/PostAdd";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";

import Welcome from "../components/Welcome";
import Note from "../components/Note";
import Loader from "../components/Loader";
import DefaultValues from "../components/order/DefaultValues";
import ProductTable from "../components/order/ProductTable";
import OrderForm from "../components/order/OrderForm";

import { GA } from "../components/tracking/GA";

import { scrollToTop } from "../helpers/scroll";

import { LINKS } from "../routes";

import { formatSelectedItems, groupItemsByProductId } from "../helpers";

const useStyles = makeStyles(theme => ({
  root: {
    paddingBottom: theme.spacing(6),
    width: "100%"
  },
  container: {
    marginBottom: theme.spacing(4)
  },
  extendedIcon: {
    marginRight: theme.spacing(1)
  },
  loader: {
    paddingTop: theme.spacing(6),
    paddingBottom: theme.spacing(6)
  },
  message: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    fontWeight: "700",
    maxWidth: 900,
    [theme.breakpoints.down("sm")]: {
      maxWidth: "none"
    }
  }
}));

function handleAddProductClick() {
  GA.event({
    category: "Product",
    action: 'User pressed the "+Add Product" button on order page',
    label: "Add product"
  });
}

function handleOpenOrderFormClick() {
  GA.event({
    category: "Order",
    action: "User pressed the open order form button on order page",
    label: "Create order"
  });
}

function handleOrderFormError(error) {
  GA.event({
    category: "Order",
    action: "There was an error while sending the order",
    label: "Create order",
    value: error
  });
}

function handleSendOrderClick() {
  GA.event({
    category: "Order",
    action: 'User pressed the "Send" button on order form',
    label: "Create order"
  });
}

function handleRemoveOrderPositionClick(value, removeOrderPosition) {
  removeOrderPosition(value);

  GA.event({
    category: "Product",
    action: "User deleted item from table",
    label: "Remove product"
  });
}

function Order({
  changeCount,
  history,
  locale,
  match,
  order,
  removeOrderPosition
}) {
  useEffect(() => {
    scrollToTop();
  }, []);

  const classes = useStyles();
  const { t } = useTranslation();
  const { items } = order;

  return (
    <Query query={PRODUCTS_QUERY}>
      {({ data, loading, error, refetch }) => {
        if (loading) {
          return (
            <div className={classes.root}>
              <Loader className={classes.loader} />
            </div>
          );
        }

        if (error) {
          return (
            <div className={classes.root}>
              <div>{t("root.error")}</div>
            </div>
          );
        }

        const { products } = data;

        const selectedItems = formatSelectedItems(items, products, locale, t);

        return (
          <div className={classes.root}>
            {/* <Container className={classes.container}>
              <Box display="flex" alignItems="center">
                <h2>{t("order.default values")}</h2>
              </Box>
              <DefaultValues />
            </Container> */}
            <Container className={classes.container}>
              <Welcome />
            </Container>
            <Container className={classes.container}>
              <Note locale={locale} />
            </Container>
            <Container className={classes.container}>
              <Box display="flex" alignItems="center" flexWrap="wrap">
                <Box flexGrow={1}>
                  <h2>{t("order.valuation items")}</h2>
                </Box>
                <Box flexGrow={0} display="flex" alignItems="center">
                  <Box mr={2}>
                    <OrderForm
                      items={selectedItems}
                      locale={locale}
                      onError={handleOrderFormError}
                      onOpenClick={handleOpenOrderFormClick}
                      onSendClick={handleSendOrderClick}
                    />
                  </Box>
                  <Link to={`${match.url}${LINKS[locale].creator}`}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleAddProductClick}
                    >
                      <AddIcon className={classes.extendedIcon} />
                      {t("order.add product")}
                    </Button>
                  </Link>
                </Box>
              </Box>
            </Container>
            {selectedItems.length ? (
              orderBy(
                products.map(({ id, name }) => ({
                  id,
                  name,
                  translation: t(`names.products.${name}`)
                })),
                "translation",
                "desc"
              ).map(({ id, translation }) => {
                const productItems = selectedItems.filter(
                  ({ product: { id: productId } }) => productId === id
                );

                return productItems.length ? (
                  <Container className={classes.container}>
                    <h3>{translation}</h3>
                    <ProductTable
                      productId={id}
                      items={productItems}
                      onCountChange={(count, id) => changeCount(count, id)}
                      onRemove={value =>
                        handleRemoveOrderPositionClick(
                          value,
                          removeOrderPosition
                        )
                      }
                    />
                  </Container>
                ) : null;
              })
            ) : (
              <Container className={classes.container}>
                <Box display="flex" alignItems="center" flexDirection="column">
                  <PostAddIcon fontSize="large" />
                  <Typography
                    className={classes.message}
                    variant="h5"
                    align="center"
                  >
                    {t("order.no products chosen")}
                  </Typography>
                  <Link to={`${match.url}${LINKS[locale].creator}`}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleAddProductClick}
                    >
                      <AddIcon className={classes.extendedIcon} />
                      {t("order.add product")}
                    </Button>
                  </Link>
                </Box>
              </Container>
            )}
          </div>
        );
      }}
    </Query>
  );
}

export const PRODUCTS_QUERY = gql`
  query ProductsQuery {
    products {
      id
      name
    }
  }
`;

const mapStateToProps = ({ order }) => {
  return { order };
};
const mapDispatchToProps = dispatch => {
  return {
    changeCount: (count, id) => dispatch(changeCount(count, id)),
    removeOrderPosition: value => dispatch(removeOrderPosition(value))
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(Order));
